import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectItem } from 'primeng/api/selectitem';
import { SelectItemService } from '../../services/select-item.service';
import { MensagemControleSituacao } from '../mensagem-controle-situacao.model';
import { MensagemControle } from '../mensagem-controle.model';
import { MensagemControleService } from '../mensagem-controle.service';
import { RespostaProcessamento } from '../resposta-processamento.model';
import {
  ConsultaPaginada,
  ConsultaPaginadaResultado,
  MensagemService,
  Ordenacao,
  ProgressoService,
  Restricao,
  RestricaoOperador,
  RestricaoPadrao,
  Restricoes,
} from 'maestro-ng';

export enum MensagemProcessamentoSituacao {
  EM_EXECUCAO = 'EM_EXECUCAO',
  PENDENTE = 'PENDENTE',
  EXECUTADO = 'EXECUTADO',
}

export class MensagemProcessamento {
  public mensagem: MensagemControle;
  public situacao: MensagemProcessamentoSituacao;
  public resultado: RespostaProcessamento;

  constructor(mensagem: MensagemControle) {
    this.mensagem = mensagem;
    this.situacao = MensagemProcessamentoSituacao.PENDENTE;
    this.resultado = undefined;
  }
}

export class DataSource {
  public restricoes: Restricoes;
  public mensagens: MensagemProcessamento[] = [];
  public carregando = false;
  public carregadas = 0;
  public executadas = 0;
  public falhas = 0;
  public sortField = 'id';
  public sortOrder = 1;

  constructor(public coordenador: MensagemControleExecutarPendentesComponent) {
    this.restricoes = new Restricoes([
      new Restricao<number>({ atributo: 'id', operador: RestricaoOperador.NOT_IN }),
      new Restricao<string>({ atributo: 'topico', operador: RestricaoOperador.EQUALS }),
      new Restricao<number>({ atributo: 'particao', operador: RestricaoOperador.EQUALS }),
      new Restricao<string>({ atributo: 'conteudo', operador: RestricaoOperador.CONTAINS }),
      new Restricao<string>({
        atributo: 'situacao',
        operador: RestricaoOperador.IN,
        valores: [MensagemControleSituacao.FALHA_PROCESSAMENTO],
      }),
      new Restricao<string>({ atributo: 'conteudo', operador: RestricaoOperador.CONTAINS }),
      new Restricao<Date>({ atributo: 'auditoriaDataInsercao', operador: RestricaoOperador.LT, padrao: RestricaoPadrao.DATE }),
      new Restricao<string>({ atributo: 'mensagemErro', operador: RestricaoOperador.CONTAINS }),
    ]);
  }

  public pesquisar(filtrarComFalhaOuEmExecucacao = false): void {
    if (!this.carregando) {
      this.recuperarPaginado(filtrarComFalhaOuEmExecucacao)
        .then((resultado) => this.carregamentoComSucesso(resultado))
        .catch((resposta) => this.carregamentoComFalha(resposta));
    }
  }

  public removerExecutadasSeNecessario(): void {
    const executadas = this.mensagens.filter((ms) => ms.situacao === MensagemProcessamentoSituacao.EXECUTADO);
    const limite = 100;
    if (executadas.length > limite) {
      const remover = executadas.splice(0, executadas.length - limite);
      this.mensagens = this.mensagens.filter((m) => !remover.includes(m));
    }
  }

  public carregamentoComSucesso(resultado: ConsultaPaginadaResultado<MensagemControle>, adicionar = false): void {
    const mensagensNovas = resultado.entidades.map((m) => new MensagemProcessamento(m));
    this.coordenador.carregadas += mensagensNovas.length;

    if (resultado.total) {
      this.coordenador.total = resultado.total;
    }

    if (adicionar) {
      mensagensNovas.forEach((mp) => this.mensagens.push(mp));
      this.coordenador.aposCarregamento(mensagensNovas);
      this.mensagens = [...this.mensagens];
      this.removerExecutadasSeNecessario();
    } else {
      this.mensagens = mensagensNovas;
    }
    this.coordenador.progressoService.fechar();
    this.carregando = false;
  }

  public carregamentoComFalha(resposta): void {
    this.carregando = false;
    this.coordenador.progressoService.fechar();
    this.coordenador.mensagemService.addErro('Erro ao pesquisar', resposta.message);
  }

  private recuperarPaginado(filtrarComFalhaOuEmExecucacao = false): Promise<ConsultaPaginadaResultado<MensagemControle>> {
    if (!this.restricoes.atributo('topico').isAtiva()) {
      this.coordenador.mensagemService.addAlerta('Erro', 'Para realizar a consulta é necessário definir um tópico!');
      return;
    }

    this.carregando = true;
    this.coordenador.progressoService.modeless();

    if (filtrarComFalhaOuEmExecucacao) {
      this.restricoes.atributo('id').valores = this.mensagens
        .filter(
          (mp) =>
            mp.situacao === MensagemProcessamentoSituacao.EM_EXECUCAO ||
            (mp.situacao === MensagemProcessamentoSituacao.EXECUTADO && mp.resultado === RespostaProcessamento.FALHA)
        )
        .map((mp) => mp.mensagem.id);
    } else {
      this.restricoes.atributo('id').valores = [];
    }

    const consultaPaginada: ConsultaPaginada = {
      inicio: 0,
      limite: 50,
      ordenacoes: [this.sortOrder === 1 ? Ordenacao.asc('id') : Ordenacao.desc('id')],
      restricoes: this.restricoes.getAtivas(),
      contabilizar: true,
      distinguir: false,
    };

    return this.coordenador.service.recuperarPaginado(consultaPaginada);
  }

  public getMensagensPendentes() {
    return this.mensagens.filter((ms) => ms.situacao === MensagemProcessamentoSituacao.PENDENTE);
  }
}

export class Executor {
  private timeout: any;
  private ativo = false;

  constructor(public coordenador: MensagemControleExecutarPendentesComponent) {}

  public aguardar(): void {
    setTimeout(() => this.executar(), 500);
  }

  public ativar(): void {
    if (!this.ativo) {
      this.ativo = true;
      this.executar();
    }
  }

  public desativar(): void {
    this.ativo = false;
  }

  public executar(): void {
    if (this.ativo && this.coordenador.executando) {
      const mp = this.coordenador.proximaMensagemPendente();

      if (mp !== undefined) {
        mp.situacao = MensagemProcessamentoSituacao.EM_EXECUCAO;

        this.coordenador.service
          .executarNovamentePorTopicoParticaoEhId(mp.mensagem)
          .then((resposta) => {
            mp.resultado = resposta;
            mp.situacao = MensagemProcessamentoSituacao.EXECUTADO;
            this.coordenador.processadas += 1;
            this.coordenador.total -= 1;
            this.executar();
          })
          .catch((resposta) => {
            mp.situacao = MensagemProcessamentoSituacao.EXECUTADO;

            if (resposta.status === 404) {
              mp.resultado = RespostaProcessamento.NAO_ENCONTRADA;
              this.coordenador.processadas += 1;
            } else {
              mp.resultado = RespostaProcessamento.FALHA;
              this.coordenador.falhas += 1;
              this.coordenador.mensagemService.addErro('Erro ao reexecutar a mensagem:' + mp.mensagem.id, resposta.message);
            }

            this.executar();
          });
      } else {
        this.coordenador.carregar();
        this.aguardar();
      }
    }
  }
}

@Component({
  selector: 'app-mensagem-controle-executar-pendentes',
  templateUrl: './mensagem-controle-executar-pendentes.component.html',
  styleUrls: ['./mensagem-controle-executar-pendentes.component.css'],
})
export class MensagemControleExecutarPendentesComponent implements OnInit, OnDestroy {
  public topicoItens: SelectItem[] = [];
  public executando = false;
  public ds: DataSource;
  public executores: Executor[] = [];
  public mensagemControleSituacaoItens: SelectItem[];
  public total = undefined;
  public processadas = 0;
  public falhas = 0;
  public carregadas = 0;

  constructor(
    public service: MensagemControleService,
    public progressoService: ProgressoService,
    public mensagemService: MensagemService,
    private selectItemService: SelectItemService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.mensagemControleSituacaoItens = this.selectItemService.getMensagemControleSituacaoItens();
    this.service
      .recuperarTopicos()
      .then((topicos) => {
        this.topicoItens = topicos.map((topico) => ({ label: topico, value: topico } as SelectItem));
      })
      .catch((resposta) => {
        this.mensagemService.addErro('Erro', 'Erro ao encontrar Tópicos: ' + resposta.message);
      });

    this.ds = new DataSource(this);

    for (let i = 0; i < 10; i++) {
      this.executores.push(new Executor(this));
    }

    this.route.queryParams.subscribe((params) => {
      if (params['topico']) {
        this.ds.restricoes.atributo('topico').valor = params['topico'];
      }
      if (params['particao']) {
        this.ds.restricoes.atributo('particao').valor = +params['particao'];
      }
      if (params['conteudo']) {
        this.ds.restricoes.atributo('conteudo').valor = params['conteudo'];
      }
      if (params['mensagemErro']) {
        this.ds.restricoes.atributo('mensagemErro').valor = params['mensagemErro'];
      }
      if (params['ordenacao']) {
        this.ds.sortOrder = params['ordenacao'] === 'ASC' ? 1 : -1;
      }
    });
  }

  ngOnDestroy(): void {
    this.executores.forEach((e) => e.desativar());
  }

  public onEnterPesquisar(event): void {
    if (event.keyCode === 13) {
      this.pesquisar();
    }
  }

  public iniciarExecucao(): void {
    this.executando = true;

    if (this.ds.getMensagensPendentes().length === 0) {
      this.pesquisar();
    }

    this.ativarExecutores();
  }

  public ativarExecutores() {
    this.executores.forEach((executor) => executor.ativar());
  }

  public desativarExecutores() {
    this.executores.forEach((executor) => executor.desativar());
  }

  public pararExecucao(): void {
    this.executando = false;
    this.desativarExecutores();
  }

  public aposCarregamento(msgs: MensagemProcessamento[]) {
    if (msgs.length > 0) {
      this.ativarExecutores();
    } else {
      this.pararExecucao();
    }
  }

  public proximaMensagemPendente(): MensagemProcessamento {
    const mensagensPendentes = this.ds.getMensagensPendentes();

    return mensagensPendentes.length > 0 ? mensagensPendentes[0] : undefined;
  }

  public pesquisar(): void {
    this.limpar();
    this.ds.pesquisar();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        topico: this.ds.restricoes.atributo('topico').valor,
        mensagemErro: this.ds.restricoes.atributo('mensagemErro').valor,
        conteudo: this.ds.restricoes.atributo('conteudo').valor,
        ordenacao: this.ds.sortOrder === 1 ? 'ASC' : 'DESC',
        particao: this.ds.restricoes.atributo('particao').valor,
      },
      queryParamsHandling: 'merge',
    });
  }

  public carregar() {
    this.ds.pesquisar(true);
  }

  public limpar(): void {
    this.ds.mensagens = [];
    this.total = undefined;
    this.carregadas = 0;
    this.falhas = 0;
    this.processadas = 0;
  }

  public getTipoSituacaoCor(situacao: MensagemControleSituacao): string {
    return this.service.getTipoSituacaoCor(situacao);
  }

  public getTipoSituacaoDescricao(situacao: MensagemControleSituacao): string {
    return this.selectItemService.getMensagemControleSituacaoItens().filter((item) => item.value === situacao)[0].label;
  }

  public toJson(jsonStr: string): any {
    return JSON.parse(jsonStr);
  }
}
